import { createPackedAlphaCompositor } from "./packed-alpha-compositor.js";

const MEDIA_TIMEOUT_MS = 10_000;
const DECODE_TIMEOUT_MS = 2_500;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function finite(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function assertTransparentCorners(cornerAlpha, maximum = 8) {
  const value = Number(cornerAlpha);
  if (!Number.isFinite(value)) {
    throw new Error("Packed-alpha VFX failed the matte guard: corner alpha is unavailable.");
  }
  if (value <= maximum) return true;
  throw new Error(
    `Packed-alpha VFX failed the matte guard: corner alpha ${value}/255 exceeds ${maximum}/255.`,
  );
}

function queryCaptureTime() {
  if (typeof window === "undefined") return null;
  const value = new URLSearchParams(window.location.search).get("time");
  if (value === null || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function resolveMode(still, reducedMotion) {
  if (still) return "deterministic";
  if (reducedMotion) return "static";
  return "full";
}

function positiveModulo(value, divisor) {
  if (!Number.isFinite(divisor) || divisor <= 0) return 0;
  return ((value % divisor) + divisor) % divisor;
}

function waitForMediaEvent(media, eventName, readyTest, timeoutMs = MEDIA_TIMEOUT_MS) {
  if (readyTest()) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error(`Timed out waiting for VFX media event: ${eventName}`));
    }, timeoutMs);
    const onReady = () => {
      if (!readyTest()) return;
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error(`VFX media failed to load: ${media.currentSrc || media.src || "unknown source"}`));
    };
    const cleanup = () => {
      window.clearTimeout(timeout);
      media.removeEventListener(eventName, onReady);
      media.removeEventListener("error", onError);
    };
    media.addEventListener(eventName, onReady);
    media.addEventListener("error", onError, { once: true });
  });
}

function waitForImage(image) {
  if (!image?.src) return Promise.resolve();
  if (image.complete && image.naturalWidth > 0) {
    return typeof image.decode === "function" ? image.decode().catch(() => {}) : Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error(`Timed out loading VFX poster: ${image.src}`));
    }, MEDIA_TIMEOUT_MS);
    const cleanup = () => {
      window.clearTimeout(timeout);
      image.removeEventListener("load", onLoad);
      image.removeEventListener("error", onError);
    };
    const onLoad = async () => {
      cleanup();
      if (typeof image.decode === "function") await image.decode().catch(() => {});
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error(`VFX poster failed to load: ${image.src}`));
    };
    image.addEventListener("load", onLoad, { once: true });
    image.addEventListener("error", onError, { once: true });
  });
}

function afterTwoFrames() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

function waitForDecodedVideoFrame(video) {
  if (typeof video.requestVideoFrameCallback !== "function") return afterTwoFrames();
  return new Promise((resolve, reject) => {
    let callbackId = 0;
    const timeout = window.setTimeout(() => {
      if (callbackId && typeof video.cancelVideoFrameCallback === "function") {
        video.cancelVideoFrameCallback(callbackId);
      }
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        resolve({ mediaTime: video.currentTime, fallback: "have-current-data" });
      } else {
        reject(new Error("Timed out waiting for a decoded VFX video frame."));
      }
    }, DECODE_TIMEOUT_MS);
    callbackId = video.requestVideoFrameCallback((_now, metadata) => {
      window.clearTimeout(timeout);
      resolve(metadata);
    });
  });
}

function setMediaSource(video, source) {
  video.pause();
  video.removeAttribute("src");
  video.load();
  if (source) {
    video.src = source;
    video.load();
  }
}

function sourceCandidates(config) {
  if (Array.isArray(config.sources) && config.sources.length) return config.sources;
  return config.src ? [{ src: config.src, type: "video/webm" }] : [];
}

function selectPlayableSource(video, config) {
  const candidates = sourceCandidates(config);
  return candidates.find(({ type }) => !type || video.canPlayType(type) !== "") || candidates[0] || null;
}

function allSourcesAreLocal(config) {
  return sourceCandidates(config).every(({ src }) => String(src || "").startsWith("/assets/"))
    && String(config.poster || "").startsWith("/assets/");
}

function applyConfigVariables(root, config) {
  const position = config.position || {};
  const mobile = config.mobilePosition || position;
  const treatment = config.treatment || {};
  root.style.setProperty("--vfx-x", position.x || "50%");
  root.style.setProperty("--vfx-y", position.y || "50%");
  root.style.setProperty("--vfx-width", position.width || "520px");
  root.style.setProperty("--vfx-rotation", position.rotation || "0deg");
  root.style.setProperty("--vfx-mobile-x", mobile.x || position.x || "50%");
  root.style.setProperty("--vfx-mobile-y", mobile.y || position.y || "50%");
  root.style.setProperty("--vfx-mobile-width", mobile.width || position.width || "520px");
  root.style.setProperty("--vfx-opacity", String(clamp(finite(treatment.opacity, 0.7), 0, 1)));
  root.style.setProperty("--vfx-brightness", String(clamp(finite(treatment.brightness, 1), 0.35, 1.5)));
  root.style.setProperty("--vfx-contrast", String(clamp(finite(treatment.contrast, 1), 0.5, 1.8)));
  root.style.setProperty("--vfx-saturation", String(clamp(finite(treatment.saturation, 1), 0, 2)));
  root.style.setProperty("--vfx-hue", treatment.hue || "0deg");
  root.style.setProperty("--vfx-scale-x", String(clamp(finite(treatment.scaleX, 1), 0.7, 1.3)));
  root.style.setProperty("--vfx-scale-y", String(clamp(finite(treatment.scaleY, 1), 0.7, 1.3)));
}

export function createApprovedVfxMaster(
  root,
  { concept, reducedMotion = false, still = false, onState } = {},
) {
  if (!root) throw new TypeError("createApprovedVfxMaster requires the experience root");
  const layer = root.querySelector("[data-vfx-layer]");
  const output = root.querySelector("[data-vfx-output]");
  const video = root.querySelector("[data-vfx-master]");
  const poster = root.querySelector("[data-vfx-poster]");
  if (!layer || !output || !video || !poster) throw new Error("Approved alpha VFX layer markup is incomplete.");

  let currentConcept = concept;
  let reduceMotion = Boolean(reducedMotion);
  const stillFrame = Boolean(still);
  const requestedTime = queryCaptureTime();
  let generation = 0;
  let currentPromise = Promise.resolve(true);
  let frameCallbackId = 0;
  let objectUrl = null;
  let cueTimer = 0;
  let compositor = null;
  let destroyed = false;
  let manuallyPaused = false;
  let state = {
    assetId: null,
    conceptId: null,
    label: null,
    mode: resolveMode(stillFrame, reduceMotion),
    readyState: "idle",
    sourceCount: 0,
    paused: true,
    manuallyPaused: false,
    requestedTime,
    mappedTime: null,
    currentTime: 0,
    duration: 0,
    presentedFrames: 0,
    compositorBackend: "transparent-poster",
    alphaMode: "straight-alpha-poster",
    outputWidth: 0,
    outputHeight: 0,
    maxCornerAlpha: 0,
    localOnly: true,
    fallback: false,
    error: null,
  };

  video.muted = true;
  video.playsInline = true;
  video.controls = false;
  video.disablePictureInPicture = true;
  video.tabIndex = -1;
  video.setAttribute("aria-hidden", "true");
  video.setAttribute("playsinline", "");
  video.setAttribute("muted", "");
  video.draggable = false;
  poster.draggable = false;

  function compositorPatch() {
    if (!compositor) return {};
    const details = compositor.getState();
    return {
      compositorBackend: details.backend,
      alphaMode: details.alphaMode,
      presentedFrames: details.frameCount,
      outputWidth: details.outputWidth,
      outputHeight: details.outputHeight,
      maxCornerAlpha: details.cornerAlpha,
    };
  }

  function publish(patch = {}) {
    state = {
      ...state,
      ...compositorPatch(),
      ...patch,
      paused: video.paused,
      manuallyPaused,
      currentTime: Number.isFinite(video.currentTime) ? Number(video.currentTime.toFixed(4)) : 0,
      sourceCount: video.hasAttribute("src") ? 1 : 0,
    };
    layer.dataset.vfxMode = state.mode;
    layer.dataset.vfxState = state.readyState;
    layer.dataset.vfxAlphaMode = state.alphaMode;
    layer.dataset.vfxBackend = state.compositorBackend;
    if (typeof onState === "function") {
      try {
        onState(getState());
      } catch {
        // Diagnostics must never interrupt playback.
      }
    }
    return getState();
  }

  function stopFrameMonitor() {
    if (frameCallbackId && typeof video.cancelVideoFrameCallback === "function") {
      video.cancelVideoFrameCallback(frameCallbackId);
    }
    frameCallbackId = 0;
  }

  function releaseObjectUrl() {
    if (!objectUrl) return;
    URL.revokeObjectURL(objectUrl);
    objectUrl = null;
  }

  function destroyCompositor() {
    compositor?.destroy();
    compositor = null;
    output.hidden = true;
  }

  function showPoster() {
    output.hidden = true;
    poster.hidden = false;
  }

  function showOutput() {
    output.hidden = false;
    poster.hidden = true;
  }

  async function loadLocalObjectUrl(source, token) {
    const response = await fetch(source, {
      method: "GET",
      cache: "force-cache",
      credentials: "same-origin",
    });
    if (!response.ok) throw new Error("VFX source returned HTTP " + response.status + ": " + source);
    const blob = await response.blob();
    assertCurrent(token);
    releaseObjectUrl();
    objectUrl = URL.createObjectURL(blob);
    return objectUrl;
  }

  function drawComposite({ finish = false, inspectCorners = false } = {}) {
    if (!compositor) throw new Error("Packed-alpha compositor is unavailable.");
    const details = compositor.draw(video, { finish, inspectCorners });
    assertTransparentCorners(details.cornerAlpha);
    state.presentedFrames = details.frameCount;
    state.outputWidth = details.outputWidth;
    state.outputHeight = details.outputHeight;
    state.maxCornerAlpha = details.cornerAlpha;
    return details;
  }

  function fallBackToPoster(error) {
    stopFrameMonitor();
    video.pause();
    video.hidden = true;
    setMediaSource(video, null);
    releaseObjectUrl();
    destroyCompositor();
    showPoster();
    publish({
      readyState: "ready",
      compositorBackend: "transparent-poster-fallback",
      alphaMode: "straight-alpha-poster",
      presentedFrames: 0,
      fallback: true,
      error: String(error?.message || error),
    });
  }

  function startFrameMonitor(token) {
    stopFrameMonitor();
    if (typeof video.requestVideoFrameCallback !== "function") return;
    const monitor = (_now, metadata) => {
      if (destroyed || token !== generation || state.mode !== "full") return;
      try {
        drawComposite();
        state.currentTime = Number(finite(metadata?.mediaTime, video.currentTime).toFixed(4));
      } catch (error) {
        fallBackToPoster(error);
        return;
      }
      frameCallbackId = video.requestVideoFrameCallback(monitor);
    };
    frameCallbackId = video.requestVideoFrameCallback(monitor);
  }

  function assertCurrent(token) {
    if (destroyed || token !== generation) {
      const error = new Error("Superseded VFX media load.");
      error.name = "AbortError";
      throw error;
    }
  }

  async function seekAndDecode(targetTime, token) {
    const safeTarget = clamp(targetTime, 0, Math.max(0, finite(video.duration, 0) - 0.025));
    if (Math.abs(video.currentTime - safeTarget) > 0.015) {
      const decodedFrame = waitForDecodedVideoFrame(video);
      const seeked = waitForMediaEvent(
        video,
        "seeked",
        () => !video.seeking && Math.abs(video.currentTime - safeTarget) < 0.08,
      );
      video.currentTime = safeTarget;
      await Promise.all([seeked, decodedFrame]);
    } else {
      await afterTwoFrames();
    }
    assertCurrent(token);
    return safeTarget;
  }

  async function synchronize(nextConcept = currentConcept) {
    currentConcept = nextConcept;
    const config = currentConcept?.cinematicVfx;
    if (!config) throw new Error(`Missing cinematicVfx configuration for ${currentConcept?.id || "unknown concept"}.`);
    const token = ++generation;
    stopFrameMonitor();
    destroyCompositor();
    window.clearTimeout(cueTimer);
    layer.removeAttribute("data-vfx-cue");
    layer.dataset.vfxMasterId = config.id;
    applyConfigVariables(root, config);

    const mode = resolveMode(stillFrame, reduceMotion);
    const selectedSource = selectPlayableSource(video, config);
    state = {
      assetId: config.id,
      conceptId: currentConcept.id,
      label: config.label,
      mode,
      readyState: "loading",
      sourceCount: 0,
      paused: true,
      manuallyPaused,
      requestedTime,
      mappedTime: null,
      currentTime: 0,
      duration: finite(config.duration, 0),
      presentedFrames: 0,
      compositorBackend: "transparent-poster",
      alphaMode: "straight-alpha-poster",
      outputWidth: 0,
      outputHeight: 0,
      maxCornerAlpha: 0,
      localOnly: allSourcesAreLocal(config),
      fallback: false,
      error: null,
    };
    video.hidden = true;
    showPoster();
    if (poster.getAttribute("src") !== config.poster) poster.src = config.poster;
    video.poster = config.poster;
    publish();

    try {
      await waitForImage(poster);
      assertCurrent(token);
      if (mode === "static") {
        releaseObjectUrl();
        video.dataset.vfxSource = selectedSource?.src || config.src || "";
        setMediaSource(video, null);
        video.removeAttribute("autoplay");
        video.removeAttribute("loop");
        publish({ readyState: "ready" });
        return true;
      }

      if (!selectedSource?.src) throw new Error("No playable packed-alpha VFX source is configured.");
      try {
        compositor = createPackedAlphaCompositor(output, {
          alphaFloor: finite(config.alphaFloor, 2 / 255),
        });
      } catch (error) {
        fallBackToPoster(error);
        return true;
      }

      video.autoplay = mode === "full" && !manuallyPaused;
      video.loop = mode === "full";
      video.toggleAttribute("autoplay", mode === "full" && !manuallyPaused);
      video.toggleAttribute("loop", mode === "full");
      video.preload = "auto";
      video.dataset.vfxSource = selectedSource.src;
      video.dataset.vfxPacking = config.packing || "rgb-alpha-side-by-side";
      const localObjectUrl = await loadLocalObjectUrl(selectedSource.src, token);
      assertCurrent(token);
      setMediaSource(video, localObjectUrl);
      await waitForMediaEvent(video, "loadedmetadata", () => video.readyState >= 1 && Number.isFinite(video.duration));
      assertCurrent(token);

      const mediaDuration = finite(video.duration, finite(config.duration, 0));
      const baseTime = requestedTime ?? 0;
      const mappedTime = positiveModulo(
        baseTime * finite(config.playbackRate, 1) + finite(config.timeOffset, 0),
        mediaDuration,
      );
      video.playbackRate = clamp(finite(config.playbackRate, 1), 0.25, 2);
      await seekAndDecode(mappedTime, token);
      video.hidden = false;

      if (mode === "deterministic") {
        video.pause();
        drawComposite({ finish: true, inspectCorners: true });
        showOutput();
        publish({
          readyState: "ready",
          mappedTime: Number(mappedTime.toFixed(4)),
          duration: Number(mediaDuration.toFixed(4)),
        });
        return true;
      }

      if (manuallyPaused) {
        video.pause();
        drawComposite({ finish: true, inspectCorners: true });
        showOutput();
        publish({
          readyState: "ready",
          mappedTime: Number(mappedTime.toFixed(4)),
          duration: Number(mediaDuration.toFixed(4)),
        });
      } else {
        const playResult = video.play();
        if (playResult?.then) await playResult;
        assertCurrent(token);
        await waitForDecodedVideoFrame(video);
        assertCurrent(token);
        drawComposite({ inspectCorners: true });
        showOutput();
        publish({
          readyState: "ready",
          mappedTime: Number(mappedTime.toFixed(4)),
          duration: Number(mediaDuration.toFixed(4)),
        });
        startFrameMonitor(token);
      }
      return true;
    } catch (error) {
      if (error?.name === "AbortError") return false;
      fallBackToPoster(error);
      return true;
    }
  }

  function setConcept(nextConcept) {
    currentPromise = synchronize(nextConcept);
    return currentPromise;
  }

  function setReducedMotion(nextReducedMotion) {
    const value = Boolean(nextReducedMotion);
    if (value === reduceMotion) return currentPromise;
    reduceMotion = value;
    currentPromise = synchronize(currentConcept);
    return currentPromise;
  }

  async function setPaused(nextPaused) {
    if (destroyed || stillFrame || reduceMotion || state.mode !== "full") return getState();
    const paused = Boolean(nextPaused);
    if (paused === manuallyPaused) return getState();
    manuallyPaused = paused;
    if (paused) {
      stopFrameMonitor();
      video.pause();
      if (compositor && state.readyState === "ready") drawComposite({ finish: true });
      return publish();
    }
    video.autoplay = true;
    video.toggleAttribute("autoplay", true);
    const playResult = video.play();
    if (playResult?.then) await playResult;
    if (!destroyed && state.mode === "full") startFrameMonitor(generation);
    return publish();
  }

  function cue(type = "pulse") {
    if (destroyed || stillFrame || reduceMotion || manuallyPaused) return;
    window.clearTimeout(cueTimer);
    layer.removeAttribute("data-vfx-cue");
    void layer.offsetWidth;
    layer.dataset.vfxCue = String(type);
    cueTimer = window.setTimeout(() => layer.removeAttribute("data-vfx-cue"), 820);
  }

  function ready() {
    return currentPromise;
  }

  function getState() {
    return Object.freeze({
      ...state,
      ...compositorPatch(),
      paused: video.paused,
      manuallyPaused,
      currentTime: Number.isFinite(video.currentTime) ? Number(video.currentTime.toFixed(4)) : state.currentTime,
      sourceCount: video.hasAttribute("src") ? 1 : 0,
    });
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    generation += 1;
    window.clearTimeout(cueTimer);
    stopFrameMonitor();
    destroyCompositor();
    setMediaSource(video, null);
    releaseObjectUrl();
  }

  currentPromise = synchronize(currentConcept);
  return { setConcept, setReducedMotion, setPaused, cue, ready, getState, destroy };
}

export default createApprovedVfxMaster;
